import { api } from './api';

export interface DocusealSubmission {
  id: number;
  agreement_id: number;
  status: 'pending' | 'awaiting' | 'completed' | 'declined' | 'expired' | 'failed';
  status_label: string;
  signing_url: string | null;
  is_completed: boolean;
  error_message: string | null;
}

export interface DocusealStartResult {
  success: boolean;
  message?: string;
  /** Set when DocuSeal cannot serve this agreement and the HTML flow should be used. */
  fallback_to_html?: boolean;
  data?: DocusealSubmission;
}

export interface DocusealVariable {
  docx_variable: string;
  source: 'field' | 'static';
  field_key: string | null;
  static_value: string | null;
  default_field_key: string | null;
  is_mapped: boolean;
}

export interface DocusealFieldTag {
  name: string;
  type: string;
  role: string | null;
  required: boolean;
}

export interface DocusealTemplate {
  id: number;
  agreement_id: number;
  firm_id: number | null;
  firm_name: string | null;
  name: string;
  delivery_mode: 'dynamic_docx' | 'template';
  template_id: number | null;
  docx_filename: string | null;
  has_docx: boolean;
  has_signature_field: boolean;
  append_signature_block: boolean;
  signer_role: string;
  is_active: boolean;
  variables: DocusealVariable[];
  field_tags: DocusealFieldTag[];
  send_blockers: string[];
  is_ready_to_send: boolean;
  updated_at: string | null;
}

export interface DocusealTemplateRow {
  agreement: {
    id: number;
    title: string;
    slug: string;
    version: number;
    is_active: boolean;
  };
  template: DocusealTemplate | null;
}

export interface DocusealSettings {
  enabled: boolean;
  api_url: string;
  /** Host serving the signing forms (docuseal.com), derived from api_url unless overridden. */
  app_url: string;
  has_api_key: boolean;
  has_webhook_secret: boolean;
  webhook_url: string;
  is_configured: boolean;
  is_enabled: boolean;
}

export interface DocusealFieldCatalogEntry {
  key: string;
  label: string;
}

export interface DocusealVariablePreview {
  docx_variable: string;
  resolved_value: string | null;
  is_resolved: boolean;
}

export const docusealService = {
  /** Begin or resume embedded signing for an agreement. */
  async start(agreementId: number): Promise<DocusealStartResult> {
    const response = await api.post('/agreements/docuseal/start', { agreement_id: agreementId });
    return response.data;
  },

  async sync(submissionId: number): Promise<DocusealSubmission> {
    const response = await api.post(`/agreements/docuseal/${submissionId}/sync`);
    return response.data.data;
  },
};

export const adminDocusealService = {
  async getSettings(): Promise<DocusealSettings> {
    const response = await api.get('/admin/docuseal/settings');
    return response.data.data;
  },

  async saveSettings(data: {
    enabled?: boolean;
    api_url?: string;
    app_url?: string;
    api_key?: string;
    webhook_secret?: string;
  }): Promise<DocusealSettings> {
    const response = await api.put('/admin/docuseal/settings', data);
    return response.data.data;
  },

  async testConnection(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/admin/docuseal/settings/test');
    return response.data;
  },

  async listTemplates(): Promise<DocusealTemplateRow[]> {
    const response = await api.get('/admin/docuseal/templates');
    return response.data.data;
  },

  async upsertTemplate(data: {
    agreement_id: number;
    firm_id?: number | null;
    name?: string;
    delivery_mode?: string;
    template_id?: number | null;
    signer_role?: string;
    append_signature_block?: boolean;
    is_active?: boolean;
  }): Promise<DocusealTemplate> {
    const response = await api.post('/admin/docuseal/templates', data);
    return response.data.data;
  },

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/admin/docuseal/templates/${id}`);
  },

  async uploadDocx(id: number, file: File): Promise<{ message: string; data: DocusealTemplate }> {
    const formData = new FormData();
    formData.append('docx', file);
    const response = await api.post(`/admin/docuseal/templates/${id}/docx`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async reparseDocx(id: number): Promise<DocusealTemplate> {
    const response = await api.post(`/admin/docuseal/templates/${id}/docx/reparse`);
    return response.data.data;
  },

  async removeDocx(id: number): Promise<DocusealTemplate> {
    const response = await api.delete(`/admin/docuseal/templates/${id}/docx`);
    return response.data.data;
  },

  async downloadDocx(id: number, filename: string): Promise<void> {
    const response = await api.get(`/admin/docuseal/templates/${id}/docx/download`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'template.docx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async saveVariableMappings(
    id: number,
    variableMappings: Array<{
      docx_variable: string;
      source: 'field' | 'static';
      field_key?: string | null;
      static_value?: string | null;
    }>,
  ): Promise<DocusealTemplate> {
    const response = await api.post(`/admin/docuseal/templates/${id}/variable-mappings`, {
      variable_mappings: variableMappings,
    });
    return response.data.data;
  },

  async previewVariables(
    id: number,
    userId?: number,
  ): Promise<{
    preview_user: { id: number; name: string; email: string };
    variables: DocusealVariablePreview[];
  }> {
    const response = await api.post(`/admin/docuseal/templates/${id}/preview`, { user_id: userId });
    return response.data.data;
  },

  async fieldCatalog(): Promise<DocusealFieldCatalogEntry[]> {
    const response = await api.get('/admin/docuseal/field-catalog');
    return response.data.data;
  },
};
